import type {
  ChargeItemDefinition,
  ChargeItemDefinitionPropertyGroupPriceComponent,
} from "@medplum/fhirtypes";

/**
 * Canonical base URL prefix for procedure-fee ChargeItemDefinition instances.
 * Global: `{base}{cpt}`. Practice-scoped: `{base}{organizationId}-{cpt}`.
 */
export const CHARGE_ITEM_DEFINITION_BASE_URL =
  "http://quiltedhealth.com/fhir/ChargeItemDefinition/procedure-";

export function getProcedureFeeChargeItemDefinitionUrl(
  procedureCode: string,
  organizationId?: string,
): string {
  if (organizationId) {
    return `${CHARGE_ITEM_DEFINITION_BASE_URL}${organizationId}-${procedureCode}`;
  }
  return `${CHARGE_ITEM_DEFINITION_BASE_URL}${procedureCode}`;
}

/**
 * Picks the longest known organization id such that `remainder` starts with
 * `{organizationId}-` and still has a non-empty procedure code after the separator.
 * Longest match avoids ambiguity when one id is a prefix of another.
 */
function findOrganizationIdPrefix(
  remainder: string,
  knownOrganizationIds: Iterable<string>,
): string | undefined {
  let best: string | undefined;
  for (const organizationId of knownOrganizationIds) {
    if (!organizationId) {
      continue;
    }
    const prefix = `${organizationId}-`;
    if (remainder.startsWith(prefix) && remainder.length > prefix.length) {
      if (!best || organizationId.length > best.length) {
        best = organizationId;
      }
    }
  }
  return best;
}

/**
 * Inverse of {@link getProcedureFeeChargeItemDefinitionUrl}.
 *
 * Practice scope is recognized only when the URL remainder starts with a known
 * `{organizationId}-` prefix. Procedure codes may themselves contain a hyphen
 * (e.g. custom modifier codes like `1562-23`), so splitting on hyphens alone is
 * unsafe. Without a matching known id, the entire remainder is treated as the
 * procedure code (global / unscoped) — never a guessed organization.
 */
export function parseProcedureFeeChargeItemDefinitionUrl(
  url: string,
  knownOrganizationIds: Iterable<string> = [],
): { procedureCode: string; organizationId?: string } | undefined {
  if (!url.startsWith(CHARGE_ITEM_DEFINITION_BASE_URL)) {
    return undefined;
  }

  const remainder = url.slice(CHARGE_ITEM_DEFINITION_BASE_URL.length);
  if (!remainder) {
    return undefined;
  }

  const organizationId = findOrganizationIdPrefix(remainder, knownOrganizationIds);
  if (!organizationId) {
    return { procedureCode: remainder };
  }

  return {
    organizationId,
    procedureCode: remainder.slice(organizationId.length + 1),
  };
}

/**
 * The base priceComponent for a payer code ("insurance" / "self-pay") on a ChargeItemDefinition.
 * The one shared lookup for both the Services tab's live fee display and statement generation's
 * price snapshot, so the two surfaces cannot drift onto different selection logic.
 */
export function selectChargeItemDefinitionPriceComponent(
  definition: ChargeItemDefinition | undefined,
  payerCode: string | undefined,
): ChargeItemDefinitionPropertyGroupPriceComponent | undefined {
  if (!definition || !payerCode) return undefined;

  const priceComponents = definition.propertyGroup?.[0]?.priceComponent;
  return priceComponents?.find(
    (component) => component.type === "base" && component.code?.coding?.[0]?.code === payerCode,
  );
}

/**
 * The numeric fee on a priceComponent. Zero is a valid amount; only a null/absent value is missing.
 */
export function extractChargeItemDefinitionFeeAmount(
  component: ChargeItemDefinitionPropertyGroupPriceComponent | undefined,
): number | undefined {
  const value = component?.amount?.value;
  return value === null || value === undefined ? undefined : value;
}

/**
 * A ChargeItemDefinition's unit price for a payer: the amount on its base priceComponent for that
 * payer code. The one answer the Services tab and the statement detail page both read a fee from.
 *
 * @param definition The fee-table definition, or undefined when it could not be resolved.
 * @param payerCode The payer code ("insurance" / "self-pay"), or undefined when unknown.
 * @returns The unit price, where zero is a valid price; undefined when the definition is unpriced
 *   for this payer.
 */
export function resolveChargeItemDefinitionUnitPrice(
  definition: ChargeItemDefinition | undefined,
  payerCode: string | undefined,
): number | undefined {
  return extractChargeItemDefinitionFeeAmount(
    selectChargeItemDefinitionPriceComponent(definition, payerCode),
  );
}
